import React, { useState } from "react"
import { css, Theme } from "@emotion/react"
import { IconButton, Modal, Paragraph } from "@ncs/web-legos/components"
import {
	CreditCard,
	PaymentSource,
	useCustomerPaymentMethods,
	useDeleteCard,
} from "@ncs/ncs-api/portal-apps"
import { captureException } from "@ncs/ncs-api"
import { useShopContext } from "~/contexts"

interface CreditCardsProps {
	cards: CreditCard[]
	selectedCardId: string | null
	onSelectCard: (card: CreditCard) => void
	isActive?: boolean
}

export const CreditCards: React.FC<CreditCardsProps> = React.memo(
	({ cards, selectedCardId, onSelectCard, isActive = false }) => {
		const formattedExpiration = (expMonth: number, expYear: number) =>
			`${String(expMonth).padStart(2, "0")}/${String(expYear).toString().slice(-2)}`

		const checkValidity = (expMonth: number, expYear: number): boolean => {
			const today = new Date()
			const expirationDate = new Date(expYear, expMonth, 0, 23, 59, 59)
			return today <= expirationDate
		}

		const [toggleModal, setToggleModal] = useState(false)
		const [selectedCard, setSelectedCard] = useState<CreditCard | null>(null)
		const [, dispatch] = useShopContext()
		const [isSubmitting, setIsSubmitting] = useState(false)
		const [paymentMethods] = useCustomerPaymentMethods()
		const deleteCard = useDeleteCard()

		const capitalize = (brandName: string) =>
			brandName && brandName[0].toUpperCase() + brandName.slice(1).toLowerCase()

		const deleteSavedCard = async (card: CreditCard | null) => {
			try {
				setIsSubmitting(true)
				if (card) {
					await deleteCard(card.id)
				}
				dispatch({
					type: "set checkout payment method",
					payload: null,
				})
			} catch (e) {
				captureException(e as Error)
				console.error(e)
			} finally {
				setToggleModal(false)
				setIsSubmitting(false)
			}
		}

		return (
			<>
				<div css={container}>
					<Modal
						showCloseButton
						isOpen={toggleModal}
						onCancel={() => setToggleModal(false)}
						onClose={() => setToggleModal(false)}
						rightButtons={{
							buttonText: "Remove",
							variant: "secondary-cta",
							onClick: () => deleteSavedCard(selectedCard),
							isLoading: isSubmitting,
						}}
					>
						<Paragraph css={modalTitle}> Remove Payment Method </Paragraph>
						<Paragraph css={modalDescription}>
							Remove {capitalize(selectedCard?.brand || "")} card ending in{" "}
							{selectedCard?.last4}?
						</Paragraph>
					</Modal>

					{cards
						.sort((a, b) => {
							const aIsDefault =
								paymentMethods?.defaultPaymentMethod?.source ===
									PaymentSource.CARD &&
								paymentMethods.defaultPaymentMethod.id === a.id
							const bIsDefault =
								paymentMethods?.defaultPaymentMethod?.source ===
									PaymentSource.CARD &&
								paymentMethods.defaultPaymentMethod.id === b.id
							return (
								bIsDefault ? 1
								: aIsDefault ? -1
								: 0
							)
						})
						.map((card, index) => {
							const isSelected = selectedCardId === card.id
							const isCardValid = checkValidity(card.expMonth, card.expYear)
							const isExpired = !isCardValid
							const isCardDefaultPaymentMethod =
								paymentMethods?.defaultPaymentMethod?.source ===
									PaymentSource.CARD &&
								paymentMethods.defaultPaymentMethod.id === card?.id
							const showChip = isCardDefaultPaymentMethod || isExpired
							const chipText =
								isCardDefaultPaymentMethod && !isExpired ? "DEFAULT"
								: isExpired ? "EXPIRED"
								: ""
							const chipColor =
								isCardDefaultPaymentMethod && !isExpired ? "#0b75e1" : "#dc2626"

							return (
								<div
									key={card.id}
									css={[
										cardContainer,
										isSelected && isActive && selectedCardStyle,
									]}
								>
									<input
										type="checkbox"
										key={index}
										id={index.toString()}
										name="selectedCard"
										checked={isSelected}
										onChange={() => onSelectCard(card)}
										css={cardCheckbox}
										//onBlur={onDeselectCard}
									/>
									{showChip && <div css={cardChip(chipColor)}>{chipText}</div>}
									<div css={cardRow} className="cardBrand">
										<img
											src={`/payments/cards/${card.brand}.svg`}
											css={cardLogo}
											alt={`${card.brand} logo`}
										/>
										<p css={cardTextStyle(isCardValid)}>
											{formattedExpiration(card.expMonth, card.expYear)}
										</p>
									</div>
									<div css={cardRow}>
										<p
											css={cardTextStyle()}
										>{`•••• •••• •••• ${card.last4}`}</p>
										<IconButton
											icon="trash"
											onClick={() => {
												setSelectedCard(card)
												setToggleModal(true)
											}}
											size="md"
											className="deleteCard"
										/>
									</div>
								</div>
							)
						})}
				</div>
			</>
		)
	}
)

const container = (theme: Theme) => css`
	display: flex;
	flex-wrap: wrap;
	margin-left: 3.3rem;
	margin-top: 1rem;
	gap: 1rem;
`

const cardContainer = (theme: Theme) => css`
	width: 200px;
	flex-shrink: 0;
	border-radius: 0.75rem;
	background: #ffffff;
	box-shadow: 0px 0px 4px 0px #d1d5db;
	position: relative;
	padding: 0.75rem;

	${theme.breakpoints.down("lg")} {
		margin-bottom: 1rem;
	}

	span.MuiIconButton-label {
		margin-top: -0.6rem;
		margin-right: -1rem;
	}

	button.MuiIconButton-root:hover {
		background-color: transparent;
	}

	> div.cardBrand {
		margin-bottom: 1.3rem;
	}
`

const cardChip = (bgColor: string) => css`
	position: absolute;
	top: -0.5rem;
	right: -0.5rem;
	background-color: ${bgColor};
	color: white;
	font-size: 12px;
	font-weight: bold;
	padding: 2px 6px;
	border-radius: 6px;
`

const cardRow = (theme: Theme) => css`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-top: 0.25rem;

	.deleteCard svg {
		&:hover {
			color: ${theme.palette.colors.error500};
		}
	}
`

const cardLogo = css`
	width: 2.8rem;
	height: 2rem;
	object-fit: contain;
`

const cardTextStyle = (isValid?: boolean) => css`
	color: ${isValid === false ? "#dc2626" : "#374151"};
	font-size: 14px;
	font-weight: 400;
`

const modalTitle = css`
	color: #393939;
	font-size: 1.5rem;
	font-weight: 600;
`

const modalDescription = css`
	color: #393939;
	font-size: 1rem;
	font-weight: 400;
`

const cardCheckbox = css`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	opacity: 0;

	&:hover {
		cursor: pointer;
	}
`

const selectedCardStyle = css`
	box-shadow: 0 0 0 2px #0b75e1;
`
